import { Inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { select, Store } from '@ngrx/store';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { CUSTOMER_ID } from './config/user-details.const';
import { BidcompGlobalApiUserService } from '../../../services/bidcomp-global-api-user.service';
import { SessionActionTypes, LoadSessionSuccess } from '../../session/actions';
import { PlatformType } from '../../../types/muni-environment';

import {
  UserDetailsTypes,
  LoadBranchesFailed,
  LoadBranchesSuccess,
  LoadCurrentUserFailed,
  LoadCurrentUserSuccess,
  LoadCurrentUser,
  LoadBranches,
  ResetCurrentUser
} from './actions';
import { selectBranchDetails } from './selectors';
import { SharedState } from '../../reducer';
import { CurrentUser } from './state';
import { UserBranchDetailsApiDto } from './models/user-branch-details-api-dto';
import { UserDetailsApiDto } from './models/user-details-api-dto';
@Injectable()
export class UserDetailsEffects {
  private readonly platform: PlatformType;
  
  public LoadCurrentUser$: Observable<
    LoadCurrentUserSuccess | LoadCurrentUserFailed
  > = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsTypes.LoadCurrentUser),
      switchMap(() => {
        return this.userService.getCurrentUser().pipe(
          switchMap((currentUser: UserDetailsApiDto) => {
            const user: CurrentUser = { ...currentUser, error: undefined };
            return of(new LoadCurrentUserSuccess(user));
          }),
          catchError((error) => {
            return of(
              new LoadCurrentUserFailed(error && (error as Error).message)
            );
          })
        );
      })
    );
  });

  public loadBranches$: Observable<
    LoadBranchesSuccess | LoadBranchesFailed
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsTypes.LoadBranches),
      withLatestFrom(this.store.pipe(select(selectBranchDetails))),
      filter(([, { branches }]) => !branches),
      switchMap(() =>
        this.userService.getSiblingBranches().pipe(
          map(
            (branchesDto: UserBranchDetailsApiDto[]) =>
              new LoadBranchesSuccess(
                branchesDto.sort((a, b) => a.city.localeCompare(b.city))
              )
          ),
          catchError((err) =>
            of(new LoadBranchesFailed(err && (err as Error).message))
          )
        )
      )
    )
  );

  public saveCustomerId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDetailsTypes.UpdateBranch),
        filter(({ customerId }) => !!customerId),
        tap(({ customerId }) => {
          sessionStorage.setItem(CUSTOMER_ID, customerId);
        })
      ),
    { dispatch: false }
  );

  // Load user details when session successfully loads OR after reset
  public loadUserDetailsOnSessionLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSessionSuccess | ResetCurrentUser>(
        SessionActionTypes.LoadSuccess,
        UserDetailsTypes.ResetCurrentUser
      ),
      filter(() => this.platform === PlatformType.polaris),
      switchMap(() => [
        new LoadCurrentUser(),
        new LoadBranches()
      ])
    )
  );

  constructor(
    public readonly actions$: Actions,
    public readonly userService: BidcompGlobalApiUserService,
    private readonly store: Store<SharedState>,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.platform =
      (config.params.platform as PlatformType) || PlatformType.polaris;
  }
}
